import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
function getDuplicateImageStyles(imageDomRect, durationMs) {
    return `
position:fixed;
width:${imageDomRect.width}px;
height:${imageDomRect.height}px;
top:${imageDomRect.top}px;
left:${imageDomRect.left}px;
z-index:900;
object-fit: cover;
-webkit-transition: all ${durationMs}ms ease-in;
-o-transition: all ${durationMs}ms ease-in;
transition: all ${durationMs}ms ease-in;
will-change: transform;
backface-visibility: hidden;
-webkit-backface-visibility: hidden;
`;
}
function createDuplicateImage(imageContainer, durationMs) {
    let imageElement = imageContainer.getElementsByTagName('img')[0];
    // if svg used als teaser image
    if (!imageElement) {
        imageElement = imageContainer.getElementsByTagName('svg')[0];
    }
    const duplicateImage = imageElement.cloneNode(true);
    duplicateImage.style.cssText = getDuplicateImageStyles(imageElement.getBoundingClientRect(), durationMs);
    return duplicateImage;
}
function getTransformStyle(image, offsetY = 0) {
    const imageRectangle = image.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    // calculate scale-factor
    const scale = windowWidth / imageRectangle.width;
    // translate the image to horizontal center
    const translateX = windowWidth / 2 - imageRectangle.width / 2 - imageRectangle.left;
    // imageTop - toolbarHeight - (middle of bigImageHeight) + (middle of smallImageHeight)
    const translateY = (imageRectangle.top - offsetY - (imageRectangle.height * scale) / 2 + imageRectangle.height / 2) * -1;
    return `translate(${Math.round(translateX)}px, ${Math.round(translateY)}px) scale(${scale})`;
}
function animateClickEffect(imageContainer, offsetY, durationMs = 600) {
    // use a time triggered css transition instead of css animation
    // we can't have @keyframes generated inside inline styles
    // hardcoding an @keyframes somewhere else in not an option because
    // we need to compute translation x and y dynamically
    const { body } = document;
    if (body) {
        const duplicateImage = createDuplicateImage(imageContainer, durationMs);
        body.appendChild(duplicateImage);
        function applyAnimation() {
            duplicateImage.style.transform = getTransformStyle(duplicateImage, offsetY);
            duplicateImage.style.opacity = '0.2';
        }
        let animationExecuted = false;
        requestAnimationFrame(() => {
            applyAnimation();
            animationExecuted = true;
        });
        // Safari Fallback - https://jira.ard.de/browse/ARDONI7-45889
        setTimeout(() => {
            if (!animationExecuted) {
                applyAnimation();
            }
        }, 50);
        setTimeout(() => duplicateImage.remove(), durationMs);
    }
}
export function useOnClick(imageRef, hasDisabledAnimations, onClick, hasClickEffect, clickEffectOffsetY, isDisabled) {
    return useCallback((event) => {
        if (isDisabled) {
            event.preventDefault();
        }
        else {
            if (hasClickEffect && imageRef.current && !hasDisabledAnimations) {
                animateClickEffect(imageRef.current, clickEffectOffsetY);
            }
            if (onClick) {
                onClick(event);
            }
        }
    }, [onClick, hasClickEffect, hasDisabledAnimations, imageRef, isDisabled]);
}
export function useTitle(teaserContainerRef, longTitle, shortTitle, mediumTitle, title) {
    // This Hook checks the Teaser Width and sets the Title accordingly
    const [width, setWidth] = useState(teaserContainerRef.current ? teaserContainerRef.current.offsetWidth : 1024);
    useEffect(() => {
        function onResize() {
            if (teaserContainerRef.current) {
                setWidth(teaserContainerRef.current.offsetWidth);
            }
        }
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [teaserContainerRef]);
    if (width > 450) {
        return longTitle || mediumTitle || shortTitle || title || null;
    }
    else if (width < 300) {
        return shortTitle || title || null;
    }
    else {
        return mediumTitle || shortTitle || title || null;
    }
}
function removeHover(hoverContainerRef, setIsFocus, setIsHover, timeoutRef) {
    // This function removes all the animation classes to revert the hover animation
    if (matchMedia('(hover:hover) and (pointer:fine)').matches) {
        // Only if Browser supports hover state
        if (hoverContainerRef) {
            const hoverContainer = hoverContainerRef.current;
            if (hoverContainer) {
                hoverContainer.classList.remove('hover', 'default', 'left', 'right', 'bottom'); // Remove all Animation Classes to animate the Teaser back to normal
            }
            setIsFocus(false); // remove outline styling for base container
            timeoutRef.current = setTimeout(() => {
                setIsHover(false); // Removes Hover State DOM Elements after the Reverse Animation is finished
            }, 300);
        }
    }
}
function addHoverAnimationClasses(hoverContainerRef) {
    // This adds the appropriate classes to start the Hover Animation
    const teaserContainer = hoverContainerRef?.current;
    if (teaserContainer) {
        const teaserRect = teaserContainer.getBoundingClientRect();
        let positionHorizontal = 'default';
        let positionVertical = 'default';
        //Check if the teaser is on the left or right edge of the browser window
        if (teaserRect.left <= 100) {
            positionHorizontal = 'left';
        }
        else if (teaserRect.right >= window.innerWidth - 100) {
            positionHorizontal = 'right';
        }
        //Check if the teaser is on the bottom edge of the browser window
        if (teaserRect.bottom >= window.innerHeight - 150) {
            positionVertical = 'bottom';
        }
        teaserContainer.classList.add(positionHorizontal, positionVertical, 'hover'); //Add Classes to start animation
    }
}
function onHover(timeoutRef, setIsHover, hoverContainerRef) {
    if (timeoutRef.current) {
        clearTimeout(timeoutRef.current); // Clear the timeout so that we don't remove the Hover State Dom Elements accidentally
    }
    flushSync(() => {
        setIsHover(true);
    });
    addHoverAnimationClasses(hoverContainerRef);
}
export function useOnMouseEnter(baseContainerRef, hoverContainerRef, timeoutRef, isHover, setIsHover) {
    return useCallback(() => {
        // Set Hover Animation only if Browser supports hover state
        if (matchMedia('(hover:hover) and (pointer:fine)').matches) {
            // We need this timeout as a fix because the onMouseLeave event isn't triggered reliably on fast mouse movements
            setTimeout(() => {
                const baseContainer = baseContainerRef?.current;
                // Check if we are still hovering over the same teaser before triggering isHover
                if (baseContainer && baseContainer.querySelector(':hover')) {
                    onHover(timeoutRef, setIsHover, hoverContainerRef);
                }
                else {
                    // The following timeout is needed because on fast rehovering the result of "querySelector(':hover')" is not correct immediately in Chrome (ARDONI7-22839)
                    setTimeout(() => {
                        // Check if we are still hovering over the same teaser before triggering isHover
                        if (baseContainer && baseContainer.querySelector(':hover')) {
                            onHover(timeoutRef, setIsHover, hoverContainerRef);
                        }
                    }, 200);
                }
            }, 100);
        }
    }, [baseContainerRef, setIsHover]);
}
export function useOnMouseLeave(hoverContainerRef, timeoutRef, setIsHover, setIsFocus) {
    // This Hook reverses the Hover Animation
    return useCallback(() => {
        removeHover(hoverContainerRef, setIsFocus, setIsHover, timeoutRef);
    }, [hoverContainerRef, timeoutRef, setIsHover, setIsFocus]);
}
export function useOnFocus(baseContainerRef, hoverContainerRef, setIsHover, setIsFocus, timeoutRef) {
    // This Hook starts the Hover Animation on focus
    return useCallback(() => {
        setTimeout(() => {
            if (matchMedia('(hover:hover) and (pointer:fine)').matches) {
                // Start the hover animation only if focus is still on the same teaser after one second
                if (baseContainerRef?.current &&
                    document.activeElement === baseContainerRef?.current.querySelector('a')) {
                    onHover(timeoutRef, setIsHover, hoverContainerRef);
                    setIsFocus(true); // sets outline styling for base container
                    if (hoverContainerRef && hoverContainerRef.current) {
                        const hoverContainerLink = hoverContainerRef.current.querySelector('a');
                        if (hoverContainerLink) {
                            hoverContainerLink.focus(); // set focus to hover Link
                        }
                    }
                }
            }
        }, 500);
    }, [baseContainerRef, hoverContainerRef, setIsFocus, setIsHover]);
}
export function useOnBlur(teaserWrapperRef, hoverContainerRef, setIsHover, setIsFocus, timeoutRef) {
    // This Hook reverses the Hover Animation if focus is not longer inside this teaser
    return useCallback((e) => {
        // only remove hover if next focus element is not inside this teaser
        if (teaserWrapperRef.current && !teaserWrapperRef.current.contains(e.relatedTarget)) {
            removeHover(hoverContainerRef, setIsFocus, setIsHover, timeoutRef);
        }
    }, [hoverContainerRef, setIsFocus, setIsHover, teaserWrapperRef, timeoutRef]);
}
